/**
 * What an activity says happened.
 *
 * Unlike every other list in this directory, these mirror no Postgres enum.
 * Activities live in Stream, which is a view of the history and not the history
 * — the append-only tables in `supabase/migrations` are that. So this list is
 * the whole contract, and it is versioned only by deployment: a feed can hold
 * activities written by an older build, which is why readers must tolerate a
 * type they do not recognise rather than fail on it. `isActivityType` is that
 * tolerance.
 *
 * Ordered as a work's life runs, not alphabetically.
 */
export const ACTIVITY_TYPES = [
    "collection.created",
    "collection.shared",
    "work.registered",
    /**
     * An agent was named on a work, or stood down from one.
     *
     * Placed here rather than with the provenance verbs because a mandate is not a
     * claim about the object — it is who acts for its holder — and the two read as
     * different sentences in a trail. Both carry the work as their subject, which
     * is what makes them activities at all: an affiliation claim has no record to
     * be about and is a notification only.
     */
    "work.agent_assigned",
    "work.agent_removed",
    /**
     * An agent asked a work's owner for a mandate, and the owner answered.
     *
     * Beside the mandate verbs because they are the same relation approached from
     * the other end, and separate from them because a request is not a grant:
     * `work.representation_requested` says somebody asked, and nothing about the
     * work has changed. When an owner answers `grant`, BOTH this verb's
     * `..._answered` and `work.agent_assigned` are recorded — the first is the
     * answer to a question two parties were having, the second is the mandate
     * coming into existence, and only the second belongs on the work's own feed.
     * That is not double-counting: they have different subjects and different
     * audiences, which `feedsFor` is where you can see.
     */
    "work.representation_requested",
    "work.representation_answered",
    /**
     * A work was offered to a gallery to sell, accepted, or handed back.
     *
     * Beside the mandate verbs because a consignment is the same shape of
     * relation — somebody else acting for a work's holder — and separate from
     * them because it moves the OBJECT rather than the authority to speak about
     * it. An agent may write a work's history from anywhere; a gallery has the
     * picture.
     *
     * There is no `consignment.sold`. Ending one with an outcome of `sold` is the
     * gallery saying it sold, which is a claim like a `sale` on a chain of title;
     * the verb that means title moved is a transfer's (`work.title_transferred`,
     * below, backed by `ondo.transfers`). `consignment.ended` carries the outcome
     * and asserts nothing further.
     */
    "consignment.offered",
    "consignment.accepted",
    "consignment.ended",
    /**
     * The parties agreed new terms, and the arrangement carried on.
     *
     * A FOURTH VERB RATHER THAN A SECOND READING OF THE OTHER THREE, which is the
     * whole of what OND-101 is for: the only way to reprice a consignment used to
     * be to end it and offer it again, so a price change read as
     * `consignment.ended` with an outcome of 'withdrawn' beside a fresh
     * `consignment.offered` — which is what a collapsed deal looks like. "Makes a
     * price change look like a failed arrangement" is the ticket's own sentence,
     * and this verb is the answer to it.
     *
     * ONE VERB FOR FOUR BEATS, and the other three are deliberately absent. A
     * proposal, an acceptance that is not yet unanimous, and a refusal are
     * correspondence between two parties about money, and `work:W` is read by
     * everybody who may read the work — so they are notifications and leave no
     * trail line. What reaches the trail is the moment the terms CHANGED, which is
     * the only one of the four that is a fact about the arrangement.
     *
     * IT CARRIES NO FIGURE, like every verb above it. The new asking price is on a
     * row under a policy; `ondo.consignment_revision_projection()` has no field
     * that could hold one.
     */
    "consignment.terms_revised",
    /**
     * A work went out on loan, and the life of that arrangement.
     *
     * Beside the consignment verbs because a loan is the same shape of fact — the
     * object is in somebody else's hands — and separate from them because a
     * consignment is possession in order to SELL. Nothing about a loan is a claim
     * about title, and `isLiveLoan` is the only thing that reads a loan as a state.
     *
     * Decided in OND-9 and OND-87, 28 September: "a loan writes a line onto the
     * work's history, and it is then available to all." Seven verbs rather than
     * three, because OND-87 asks in the same breath that the moments stay apart —
     * "Keep loan offer, acceptance, receipt, decline, and return distinct; an
     * offer is not a receipt."
     *
     * `loan.proposed`, `loan.withdrawn` and `loan.declined` are CORRESPONDENCE
     * and the other four are facts about the object, which is the whole of why
     * there are seven. A proposal is a claim about the institution it names —
     * 20260804090200: "
     * asserting unilaterally that Tate has your painting is a claim about Tate" —
     * so neither it nor its unsaying reaches the work's feed. `feedsFor` is where
     * that is visible, and it decides it from the verb.
     *
     * `loan.agreed` is the arrangement coming into existence: the borrower's
     * assent, or — for a venue with no ONDO id, which is the ordinary case — the
     * holder's own act of recording it. `loan.dispatched` is the object leaving.
     * `loan.ended` carries `returned` or `cancelled`, the two ways an arrangement
     * that stood finishes.
     *
     * `loan.declined` and `loan.received` arrived with OND-9 and
     * `20261003094000_borrower_loan_lifecycle.sql`, which built the four borrower
     * acts the paragraphs above were waiting for. With them the list is one-to-one
     * with OND-87's five moments — offer, acceptance, receipt, decline, return —
     * and nothing is folded into anything else.
     *
     * `loan.declined` is the BORROWER refusing, and it is not `loan.withdrawn`
     * with a different actor: that verb is the LENDER unsaying their own
     * question. Storing one as the other would have told a museum's refusal as
     * the lender changing their mind. Like the proposal it answers, it is
     * CORRESPONDENCE and never reaches `work:W` — the work's history was never
     * told the loan was mooted, and announcing the refusal of an unannounced
     * question would be the announcement.
     *
     * `loan.received` is the borrower saying the object ARRIVED, and it is the
     * clearest fact about the object in the whole feature: `loan.agreed` said an
     * arrangement exists, `loan.dispatched` said it left, and this is the only
     * line that says it got there. It is backed by `ondo.loans.received_at` — a
     * timestamp rather than a status, because a crate in transit and a picture on
     * the wall are both `out`. Nothing may write it from a timer, which is OND-9's
     * standing rule and is why this verb did not exist until a person could press
     * something to cause it.
     */
    "loan.proposed",
    "loan.withdrawn",
    "loan.agreed",
    "loan.declined",
    "loan.dispatched",
    "loan.received",
    "loan.ended",
    /**
     * A work was sold, title moved, or the sale was unsaid.
     *
     * The verbs the paragraph above said did not exist yet. `work.sold` is the
     * sale — an `ondo.transfers` row, effected under a consignment — and it is
     * emphatically not `consignment.ended` with an outcome of `sold`, which
     * remains what it always was: the gallery SAYING it sold, a claim like a
     * `sale` on a chain of title. Both are recorded when a sale happens, because
     * both are true and they are about different rows.
     *
     * `work.title_transferred` is the buyer accepting, which is the only moment
     * anything in this registry changes hands. It is separate from `work.sold`
     * for the reason the two columns are separate: most buyers are not on ONDO,
     * so a great many sales stand with title exactly where it was, and a single
     * verb would have had to lie about one case or the other.
     *
     * `work.sale_ended` carries a declined or withdrawn outcome — the two ways a
     * recorded sale is unsaid. There is no delete behind it; the row stays and
     * the work is green again.
     */
    "work.sold",
    "work.title_transferred",
    "work.sale_ended",
    /**
     * A post-sale covenant fell due, and was answered.
     *
     * `settlement.due` is the moment a covenanted work sells AGAIN inside its
     * term — an `ondo.resale_settlements` row, written by the same definer that
     * completes the resale. It is NOT `work.sold` on the second sale, which is
     * already recorded and is about the sale rather than about the obligation the
     * sale created; the two are separate for the reason `work.sold` and
     * `consignment.ended` are, and they name different rows.
     *
     * `settlement.settled` carries an outcome of `collected` or `remitted` — the
     * two legs, recorded separately because they are true at different moments
     * and either can happen without the other. NEITHER MOVES MONEY: ONDO records
     * that a director says it moved.
     *
     * `settlement.answered` is the artist confirming or disputing a remittance,
     * with the outcome saying which. It is on the WORK's trail rather than in a
     * private message for the reason `provenance.disputed` is: a party saying
     * they were not paid what a covenant owed them is a fact about the record,
     * not a complaint about a person.
     *
     * NO VERB FOR A VOIDED SETTLEMENT, and the absence is deliberate. A void is
     * caused by `ondo.withdraw_transfer()`, which already emits `work.sale_ended`
     * with an outcome of `withdrawn` — the settlement stopping is a consequence
     * of that one act, and a second line would report one event twice.
     */
    "settlement.due",
    "settlement.settled",
    "settlement.answered",
    "provenance.added",
    "provenance.verification_requested",
    "provenance.verified",
    "provenance.disputed",
    "provenance.verification_declined",
    "provenance.verification_referred",
    /**
     * A person's daily summary could not be delivered to them — OND-205.
     *
     * LAST IN THE LIST BECAUSE IT IS ABOUT NO WORK AT ALL, which is the one thing
     * that sets it apart from the thirty above. Every other verb here is an act
     * somebody performed on a record, and the list is "ordered as a work's life
     * runs". This one is the registry telling one reader that something addressed
     * to them went nowhere, so it sits after the life of a work rather than
     * anywhere in it.
     *
     * ITS DURABLE RECORD IS `ondo.notification_digests.missed_notice_at`, not
     * `last_error` — which `ondo.notification_digest_sent()` clears when a digest
     * finally goes out, and would therefore leave this verb standing on nothing.
     * `20261008091000` decision 3 has the argument.
     *
     * IT IS THE ONLY VERB IN THIS LIST NOBODY PERFORMED. `ActivityEntry.actor` is
     * null on it, which every reader already tolerates — the web prints an
     * em-dash and the phone prints no name. There is no principal behind a timer.
     *
     * AND IT IS WRITTEN TO ONE FEED, the recipient's own. A delivery failure is a
     * fact about the two of you and about no object, so it reaches `work:W`
     * nowhere and `collection:C` nowhere. See `feedsFor`.
     */
    "digest.missed",
];
/**
 * The subject an activity is about. Never a principal — that is the actor.
 *
 * TWO OF THESE ARE RECORDS UNDER A POLICY AND THE THIRD IS NOT, which is the
 * distinction every reader of this list has to keep. A `work` and a
 * `collection` are rows somebody may or may not be entitled to read, so
 * `ActivityService.stillVisible` re-checks them against Postgres before serving
 * a feed, and `activityHref`/`routeForActivity` turn a work into a screen.
 *
 * `digest` IS NEITHER — OND-205. It names a row in
 * `ondo.notification_digests`, which is readable by nobody including its own
 * recipient, and the entry reaches exactly one feed by a direct write rather
 * than by a follow. So there is nothing to re-check and no screen to open: the
 * line says what happened and stops. Adding it widened what every reader of
 * every existing feed parses, which `activity.events.ts` closes by naming as
 * the reason an affiliation claim is NOT an activity — and the difference is
 * that a claim had no row to be about at all. `20261008091000` decision 1.
 */
export const ACTIVITY_SUBJECT_KINDS = ["collection", "work", "digest"];
export function isActivityType(value) {
    return (typeof value === "string" && ACTIVITY_TYPES.includes(value));
}
export function isActivitySubjectKind(value) {
    return (typeof value === "string" &&
        ACTIVITY_SUBJECT_KINDS.includes(value));
}
/**
 * The Stream feed groups activity is written to.
 *
 * Shared rather than internal to the API because two things must agree about
 * them: the API writes to and follows these groups, and `local-admin
 * stream:check` verifies they exist. A group that exists in one list and not the
 * other is a write that fails at runtime against a name nobody typed twice.
 *
 * Verified rather than created, because V2 has no API for making a feed group —
 * they are dashboard configuration. There is no `stream:provision`.
 *
 * `user` is a principal's own trail, keyed by ONDO id. `collection` and `work`
 * are keyed by the record's uuid.
 *
 * `notification` is keyed by ONDO id like `user` and is otherwise unlike all
 * three: it is a NOTIFICATION-type group rather than a flat one, so Stream keeps
 * seen/read state per reader and returns unseen/unread counts on every read —
 * which is the whole reason it exists rather than the inbox being a filter over
 * `user`. It also needs its aggregation format set to `{{ id }}`, so that one
 * activity is one group and read state is per notification rather than per
 * bucket. `stream:check` prints both when the group is missing.
 *
 * Nothing writes an ActivityEvent here. The two vocabularies are separate — see
 * `NOTIFICATION_TYPES` — and this list is shared only because `fid` and
 * `stream:check` need one name for every group the app uses.
 */
export const FEED_GROUPS = ["user", "collection", "work", "notification"];
/** A Stream feed identifier, `group:id`. */
export function fid(group, id) {
    return `${group}:${id}`;
}
//# sourceMappingURL=activity.js.map